import React from 'react'
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  UnderlineType,
  AlignmentType,
  Header,
  Footer,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  VerticalAlign,
  ShadingType,
  RelativeHorizontalPosition,
  RelativeVerticalPosition,
  LevelFormat, // Added for numbering
} from 'docx'
import { MuiButton } from '..'
import { Download } from '../../assets/images/icons'
import { removeHtmlTags } from '../../util'
import { saveAs } from 'file-saver'

const CreateDummyDocx = ({ downloadData, pageHeading, startIcon, buttonText, studyDetails }) => {

  const generateDocx = () => {
    const txt = studyDetails.projectType.split(',')
    const fileName = (`${studyDetails.brandName}_${txt[1]}_${txt[0]}_${studyDetails.subTitle}`).replaceAll(',', '').replaceAll(' ', '-').replaceAll('_-', '_').replaceAll('-_', '_')

    const sectionArray = []
    const defaultStyling = {
      color: '000000',
      font: 'Arial',
      size: '11pt',
    }

    const noMargin = { top: 0, bottom: 0, left: 0, right: 0 };

    const defaultSectionProperties = {
      footers: {
        default: new Footer({
          children: [
            new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [
                new TextRun({
                  text: '© MavenMagnet',
                  color: '555555',
                  font: 'Arial',
                  size: '11pt',
                }),
              ],
            }),
          ],
        }),
      },
      headers: {
        default: new Header({
          children: [
            new Table({
              width: { size: 115, type: WidthType.PERCENTAGE },
              float: {
                horizontalAnchor: RelativeHorizontalPosition.PAGE,
                verticalAnchor: RelativeVerticalPosition.PAGE,
                absoluteHorizontalPosition: -720,
                absoluteVerticalPosition: -1440,
              },
              borders: {
                top: { style: BorderStyle.NONE },
                bottom: { 
                  style: BorderStyle.SINGLE, 
                  size: 8,
                  color: "215F9A",
                },
                left: { style: BorderStyle.NONE },
                right: { style: BorderStyle.NONE },
                insideHorizontal: { style: BorderStyle.NONE },
                insideVertical: { style: BorderStyle.NONE },
              },
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      width: { size: 33, type: WidthType.PERCENTAGE },
                      verticalAlign: VerticalAlign.CENTER,
                      margins: noMargin,
                      shading: { fill: "E0F2FE", type: ShadingType.CLEAR },
                      children: [
                        new Paragraph({
                          indent: { left: 720 }, 
                          spacing: { before: 240, after: 240 },
                          children: [
                            new TextRun({ text: studyDetails.brandName, font: "Arial", size: "11pt", bold: true }),
                            new TextRun({ text: studyDetails.projectType, font: "Arial", size: "9pt", break: 1 }),
                          ],
                        }),
                      ],
                    }),
                    new TableCell({
                      width: { size: 34, type: WidthType.PERCENTAGE },
                      verticalAlign: VerticalAlign.CENTER,
                      margins: noMargin,
                      shading: { fill: "E0F2FE", type: ShadingType.CLEAR },
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.LEFT,
                          spacing: { before: 240, after: 240 },
                          children: [],
                        }),
                      ],
                    }),
                    new TableCell({
                      width: { size: 33, type: WidthType.PERCENTAGE },
                      verticalAlign: VerticalAlign.CENTER,
                      margins: noMargin,
                      shading: { fill: "E0F2FE", type: ShadingType.CLEAR },
                      children: [
                        new Paragraph({
                          alignment: AlignmentType.RIGHT,
                          indent: { right: 720 },
                          spacing: { before: 240, after: 240 },
                          children: [
                            new TextRun({ text: studyDetails.subTitle, font: "Arial", size: "10pt", bold: true, color: "008c3c" }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      },
    }

    const getTextObject = (text, options) => {
      const tObject = {
        text: text,
        size: options?.fontSize ? (options.fontSize > 11 ? '11pt' : '10pt') : defaultStyling.size,
        color: options?.color || defaultStyling.color,
        font: defaultStyling.font,
      }
      if (options?.bold) tObject.bold = true
      if (options?.italic) tObject.italics = true
      if (options?.underline) tObject.underline = { type: UnderlineType.SINGLE }
      return tObject
    }

    const generateDocObject = () => {
      const sectionObject = {
        properties: {
          page: {
            margin: { top: 1440, right: 720, bottom: 720, left: 720 },
          },
        },
        headers: defaultSectionProperties.headers,
        footers: defaultSectionProperties.footers,
        children: [],
      }

      let paragraphObject = {
        children: [],
        spacing: { line: 1.5 * 240 },
      }

      for (let s = 0; s < downloadData.length; s++) {
        const isParentLast = s === downloadData.length - 1
        for (let p = 0; p < downloadData[s].length; p++) {
          const { text, type, options } = downloadData[s][p]
          if(text.trim() === '') continue;

          const isChildLast = p === downloadData[s].length - 1

          if (s > 0 && p === 0) {
            sectionObject.children.push(new Paragraph({ ...paragraphObject }))
            sectionObject.children.push(new Paragraph({ text: '' }))
            paragraphObject.children = []
          }

          if (type === 'H4') {
            sectionObject.children.push(
              new Paragraph({
                children: [
                  new TextRun({ text, bold: false, color: '000000', font: 'Arial', size: '16pt' }),
                ],
                heading: HeadingLevel.HEADING_1,
                spacing: { line: 1.5 * 240 },
                pageBreakBefore: s !== 0,
              })
            )
            paragraphObject = { children: [], spacing: { line: 1.5 * 240 } }
            continue
          }

          const tObject = getTextObject(text, options)

          // --- LOGIC FOR BULLETS AND NUMBERING ---
          if ((options?.bullet || options?.liType === 'number') && type !== 'P') {
            if (paragraphObject.children.length > 0) {
              sectionObject.children.push(new Paragraph(paragraphObject))
            }

            paragraphObject = {
              children: [],
              spacing: { line: 1.5 * 240 },
            }

            // Check if it should be a number or a bullet
            if (options?.liType === 'number') {
              paragraphObject.numbering = {
                reference: "decimal-numbering",
                level: options.indentLevel || 0,
              }
            } else {
              paragraphObject.bullet = { level: options.indentLevel || 0 }
            }
          } else if (type === 'P') {
            if (paragraphObject.children.length > 0) {
              sectionObject.children.push(new Paragraph(paragraphObject))
            }
            paragraphObject = { children: [], spacing: { line: 1.5 * 240 } }
          }

          paragraphObject.children.push(new TextRun(tObject))

          if (isChildLast) {
            paragraphObject.children.push(new TextRun({ text: '' }))
          }
        }
        if (isParentLast) {
          sectionObject.children.push(new Paragraph({ ...paragraphObject }))
        }
      }

      sectionArray.push(sectionObject)
    }

    generateDocObject()

    const doc = new Document({
      title: removeHtmlTags(pageHeading),
      creator: 'MavenMagnet',
      description: 'Generated by MavenMagnet!',
      // --- ADDING NUMBERING CONFIGURATION ---
      numbering: {
        config: [
          {
            reference: "decimal-numbering",
            levels: [
              {
                level: 0,
                format: LevelFormat.DECIMAL,
                text: "%1.",
                alignment: AlignmentType.START,
                style: {
                  paragraph: {
                    indent: { left: 720, hanging: 360 },
                  },
                },
              },
              {
                level: 1,
                format: LevelFormat.DECIMAL,
                text: "%2.",
                alignment: AlignmentType.START,
                style: {
                  paragraph: {
                    indent: { left: 1440, hanging: 360 },
                  },
                },
              },
            ],
          },
        ],
      },
      sections: sectionArray,
    })

    Packer.toBlob(doc).then((blob) => {
      saveAs(blob, fileName + '.docx')
    })
  }

  return (
    <MuiButton
      variant='outlined'
      color='light'
      sx={{ marginLeft: '15px' }}
      startIcon={startIcon ? startIcon : <Download />}
      onClick={generateDocx}
      className='BtnExport'
    >
      {buttonText ? buttonText : 'Export to Word'}
    </MuiButton>
  )
}

export default CreateDummyDocx
